import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';

import { CloseIcon, ExpandIcon, ShrinkIcon } from '../../layout/icons';
import { fullscreenElement, fullscreenSupported, onFullscreenChange, toggleFullscreen } from './fullscreen';
import { clampFrame, dragFrame, fitMediaCard, resizeFrame, VIDEO_ASPECT, type BoxSize, type MediaFrame } from './mediaFit';

/** A media aspect (width / height), the whole free area, or a content-sized card. */
export type MediaOverlayFit = number | 'fill' | 'card';

export interface MediaOverlayProps {
  title: string;
  children: ReactNode;
  /** Video look: black card, media fitted at 16:9 unless `fit` says otherwise. */
  bare?: boolean;
  fit?: MediaOverlayFit;
  /** Omitted only when nothing sits behind the overlay - a dismiss with nowhere to go is worse. */
  onClose?: () => void;
}

/** Chrome 58 has no ResizeObserver, and a panel toggle resizes the free area without a window resize. */
const FALLBACK_POLL_MS = 500;

function useRegionSize(active: boolean): [(node: HTMLElement | null) => void, BoxSize, { current: HTMLElement | null }] {
  const nodeRef = useRef<HTMLElement | null>(null);
  const [size, setSize] = useState<BoxSize>({ width: 0, height: 0 });

  const measure = () => {
    const node = nodeRef.current;
    if (!node) return;
    const next = { width: node.clientWidth, height: node.clientHeight };
    setSize((current) => (current.width === next.width && current.height === next.height ? current : next));
  };

  // a shell re-render (panel toggles) re-measures before paint
  useLayoutEffect(() => { if (active) measure(); });

  useEffect(() => {
    const node = nodeRef.current;
    if (!active || !node) return undefined;
    const Observer = (window as unknown as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
    if (typeof Observer === 'function') {
      const observer = new Observer(measure);
      observer.observe(node);
      return () => observer.disconnect();
    }
    const timer = window.setInterval(measure, FALLBACK_POLL_MS);
    window.addEventListener('resize', measure);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('resize', measure);
    };
  }, [active]);

  return [(node) => { nodeRef.current = node; }, size, nodeRef];
}

/** Arrow-key step for moving or resizing the card, one 8px grid unit twice over. */
const KEY_STEP = 16;

const ARROW_STEPS: Record<string, [number, number] | undefined> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

interface GestureState {
  pointerId: number;
  kind: 'move' | 'resize';
  startX: number;
  startY: number;
  frame: MediaFrame;
}

export function MediaOverlay({ title, children, bare = false, fit, onClose }: MediaOverlayProps) {
  const mode: MediaOverlayFit = fit !== undefined ? fit : bare ? VIDEO_ASPECT : 'fill';
  const aspect = typeof mode === 'number' ? mode : 0;
  // a fill card already owns the whole area, so there is nowhere to move it
  const movable = mode !== 'fill';
  const [regionRef, region, regionNode] = useRegionSize(true);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const gestureRef = useRef<GestureState | null>(null);
  // null keeps the default centred placement; set once the teacher moves or resizes the card
  const [frame, setFrame] = useState<MediaFrame | null>(null);
  const [gesture, setGesture] = useState<GestureState['kind'] | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const canFullscreen = mode !== 'card' && fullscreenSupported();

  useEffect(() => onFullscreenChange(() => setFullscreen(Boolean(cardRef.current) && fullscreenElement() === cardRef.current)), []);

  const card = aspect > 0 ? fitMediaCard(region, aspect) : null;
  const placed = frame && region.width > 0 ? clampFrame(frame, region, aspect) : null;
  // a content-sized card keeps its own height; hidden until measured, so the first frame never flashes a full-bleed card
  const cardStyle: CSSProperties | undefined = placed
    ? { position: 'absolute', left: placed.x, top: placed.y, width: placed.width, height: mode === 'card' ? undefined : placed.height }
    : card ? { width: card.width, height: card.height, visibility: card.width > 0 ? 'visible' : 'hidden' } : undefined;

  /** Where the card sits now, relative to the free area - the start of every gesture. */
  const currentFrame = (): MediaFrame | null => {
    const node = cardRef.current;
    const area = regionNode.current;
    if (!node || !area) return null;
    const box = node.getBoundingClientRect();
    const origin = area.getBoundingClientRect();
    return { x: box.left - origin.left, y: box.top - origin.top, width: box.width, height: box.height };
  };

  const apply = (kind: GestureState['kind'], start: MediaFrame, dx: number, dy: number): MediaFrame =>
    (kind === 'move' ? dragFrame(start, dx, dy, region) : resizeFrame(start, dx, dy, region, aspect));

  const begin = (kind: GestureState['kind']) => (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0 || fullscreen) return;
    // the bar's own buttons keep their taps
    if (kind === 'move' && (event.target as HTMLElement).closest('button')) return;
    const start = currentFrame();
    if (!start) return;
    event.preventDefault();
    gestureRef.current = { pointerId: event.pointerId, kind, startX: event.clientX, startY: event.clientY, frame: start };
    // capture is best-effort on old WebViews
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch (error) { /* moves still arrive while over the bar */ }
    setGesture(kind);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const active = gestureRef.current;
    if (!active || event.pointerId !== active.pointerId) return;
    setFrame(apply(active.kind, active.frame, event.clientX - active.startX, event.clientY - active.startY));
  };

  const end = (event: ReactPointerEvent<HTMLElement>) => {
    const active = gestureRef.current;
    if (!active || event.pointerId !== active.pointerId) return;
    gestureRef.current = null;
    setGesture(null);
    try { if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId); } catch (error) { /* already released */ }
  };

  const onKey = (kind: GestureState['kind']) => (event: ReactKeyboardEvent<HTMLElement>) => {
    const step = ARROW_STEPS[event.key];
    if (!step || fullscreen || (event.target as HTMLElement) !== event.currentTarget) return;
    const start = currentFrame();
    if (!start) return;
    event.preventDefault();
    setFrame(apply(kind, start, step[0] * KEY_STEP, step[1] * KEY_STEP));
  };

  const gestureProps = (kind: GestureState['kind']) => ({
    onPointerDown: begin(kind),
    onPointerMove,
    onPointerUp: end,
    onPointerCancel: end,
    onKeyDown: onKey(kind),
  });

  const className = [
    'board-media-overlay',
    bare ? 'board-media-overlay--bare' : '',
    mode === 'card' ? 'board-media-overlay--card' : '',
    aspect > 0 ? 'board-media-overlay--fitted' : '',
    placed ? 'board-media-overlay--placed' : '',
    gesture ? 'board-media-overlay--' + (gesture === 'move' ? 'moving' : 'resizing') : '',
    fullscreen ? 'board-media-overlay--fullscreen' : '',
  ].filter(Boolean).join(' ');

  // the bar sits above the media, never on it, so the close never covers YouTube's own title row
  return (
    <section ref={regionRef} className={className} aria-label={title}>
      <div ref={cardRef} className="board-media-overlay__card" style={cardStyle}>
        <header
          className={movable ? 'board-media-overlay__bar board-media-overlay__bar--movable' : 'board-media-overlay__bar'}
          tabIndex={movable ? 0 : undefined}
          aria-label={movable ? 'Kéo thanh tiêu đề để di chuyển, dùng phím mũi tên để chỉnh vị trí' : undefined}
          {...(movable ? gestureProps('move') : {})}
        >
          <strong title={title}>{title}</strong>
          {canFullscreen ? (
            <button type="button" aria-label={fullscreen ? 'Thoát toàn màn hình' : 'Toàn màn hình'} onClick={() => { if (cardRef.current) toggleFullscreen(cardRef.current); }}>
              {fullscreen ? <ShrinkIcon /> : <ExpandIcon />}
            </button>
          ) : null}
          {onClose ? <button type="button" aria-label="Đóng nội dung" onClick={onClose}><CloseIcon /></button> : null}
        </header>
        <div className="board-media-overlay__body">{children}</div>
        {aspect > 0 && !fullscreen ? (
          <span
            className="board-media-overlay__resize"
            role="button"
            tabIndex={0}
            aria-label="Kéo góc để đổi kích thước, dùng phím mũi tên để phóng to thu nhỏ"
            {...gestureProps('resize')}
          >
            {/* two-way diagonal arrow: drag grows or shrinks, unlike a corner that reads as "expand" */}
            <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 5l14 14M5 5h6M5 5v6M19 19h-6M19 19v-6" />
            </svg>
          </span>
        ) : null}
      </div>
    </section>
  );
}
